import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Star, Trash2, Clock } from 'lucide-react';
import { CalculationHistoryItem } from '@/hooks/useCalculationHistory';
import { cn } from '@/lib/utils';

interface CalculationHistoryProps {
  history: CalculationHistoryItem[];
  favorites: string[];
  onToggleFavorite: (id: string) => void;
  onClearHistory: () => void;
}

const formatCalculation = (item: CalculationHistoryItem) => {
  switch (item.type) {
    case 'basic':
      return `${item.inputs.percentage}% of ${item.inputs.value}`;
    case 'percentage-of':
      return `${item.inputs.value1} is what % of ${item.inputs.value2}`;
    case 'change':
      return `Change from ${item.inputs.originalValue} to ${item.inputs.newValue}`;
    case 'discount':
      return `$${item.inputs.originalPrice} with ${item.inputs.discountPercentage}% discount`;
    case 'tip':
      return `$${item.inputs.billAmount} bill, ${item.inputs.tipPercentage}% tip, ${item.inputs.numberOfPeople} people`;
    default:
      return 'Unknown calculation';
  }
};

const formatTime = (timestamp: number) => {
  const now = Date.now();
  const diffMinutes = Math.floor((now - timestamp) / (1000 * 60));
  
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
  return `${Math.floor(diffMinutes / 1440)}d ago`;
};

export const CalculationHistory: React.FC<CalculationHistoryProps> = ({
  history,
  favorites,
  onToggleFavorite,
  onClearHistory,
}) => {
  if (history.length === 0) return null;

  return (
    <Card className="shadow-card mt-6">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-muted-foreground" />
          <CardTitle className="text-lg font-semibold text-card-foreground">
            Recent Calculations
          </CardTitle>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearHistory}
          className="text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {history.map((item) => {
          const isFavorite = favorites.includes(item.id);
          
          return (
            <div
              key={item.id}
              className={cn(
                "p-3 rounded-lg border transition-all duration-200",
                isFavorite 
                  ? "bg-primary/5 border-primary/30" 
                  : "bg-secondary/50 border-border hover:bg-secondary"
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {formatCalculation(item)}
                  </p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-lg font-semibold text-primary">
                      {item.result}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatTime(item.timestamp)}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onToggleFavorite(item.id)}
                  className={cn(
                    "p-1.5 h-auto shrink-0 transition-colors",
                    isFavorite 
                      ? "text-primary hover:text-primary/80" 
                      : "text-muted-foreground hover:text-primary"
                  )}
                >
                  <Star 
                    className={cn(
                      "w-4 h-4",
                      isFavorite && "fill-current"
                    )} 
                  />
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};